import React, { useMemo, useRef, useState } from "react";
import { Alert, CircularProgress, Dialog } from "@mui/material";
import {
  CmpInvoiceDisplayStateEnum,
  GetUserQuery,
  useGetCmpInvoicePresentationDefaultsQuery,
} from "../../../../graphql/types";
import { foldProvincialChamberFeeForDisplay } from "../../Invoicing/provincialChamberFeeDisplay";
import {
  InvoiceDraftDocumentData,
  InvoicePaper,
  InvoicePreviewPrintStyles,
  InvoicePreviewZoomBar,
  invoicePreviewPageWidth,
  printInvoicePreviewDocument,
  useInvoicePreviewFitScale,
} from "../../Invoicing/ManageInvoices/ManageInvoiceActionModals";
import { MemberRenewalPreview } from "./memberRenewalScenario";
import { getChamberLocalDateInputValue } from "../../Invoicing/invoiceDueDate";

type Props = {
  open: boolean;
  loading: boolean;
  busy: boolean;
  preview: MemberRenewalPreview | null;
  error: string;
  chamber?: GetUserQuery["getUser"]["chamberInfo"];
  /** Chamber time zone: invoice dates are chamber-local days. */
  timeZone?: string | null;
  taxLabel: string;
  taxRegistrationNumber?: string | null;
  manualPaymentMethods: InvoiceDraftDocumentData["manualPaymentMethods"];
  canSaveDraft: boolean;
  canApprove: boolean;
  saveDraftReason?: string | null;
  approveReason?: string | null;
  onClose: () => void;
  onSaveDraft: () => void;
  onApprove: () => void;
};

const dateInput = (value: number, timeZone?: string | null) =>
  getChamberLocalDateInputValue({ nowUtcMilli: value, timeZone });
const actionStyle: React.CSSProperties = {
  border: 0, borderRadius: 8, padding: "13px 26px", fontSize: 14,
  fontWeight: 600, color: "white", background: "#E1A731",
};

const MemberRenewalInvoicePreviewContent = ({
  open, loading, busy, preview, error, chamber, timeZone, taxLabel, taxRegistrationNumber,
  manualPaymentMethods, canSaveDraft, canApprove, saveDraftReason, approveReason,
  onClose, onSaveDraft, onApprove, showProvincialChamberFeeSeparately,
}: Props & { showProvincialChamberFeeSeparately?: boolean }) => {
  const [zoom, setZoom] = useState<25 | 50 | 75 | 100 | "fit">("fit");
  const viewportRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const deps = useMemo(() => [preview], [preview]);
  const { fitScale, pageHeight } = useInvoicePreviewFitScale({
    deps, open: open && !!preview, pageRef, pageWidth: invoicePreviewPageWidth, viewportRef,
  });
  const scale = zoom === "fit" ? fitScale : zoom / 100;
  const invoice = preview?.invoice;
  const permittedManualMethods = invoice?.paymentRules?.manualPaymentAllowed
    ? invoice.paymentRules.allowedManualPaymentMethodLabels.map((label) => ({
      ...manualPaymentMethods?.find((method) => method.label.trim().toLowerCase() === label.trim().toLowerCase()),
      label,
      enabled: true,
    }))
    : [];
  const permittedPaymentMethods = [
    ...(invoice?.paymentRules?.creditCardAllowed ? [{ label: "Credit Card", enabled: true }] : []),
    ...permittedManualMethods,
  ];
  const draftDocument: InvoiceDraftDocumentData | null = invoice && invoice.billingSnapshot && invoice.lineItems && invoice.invoiceKind && invoice.subtotalAmountInCents != null && invoice.taxAmountInCents != null && invoice.totalAmountInCents != null ? {
    id: invoice.id,
    invoiceNumber: invoice.invoiceNumber,
    invoiceKind: invoice.invoiceKind,
    customerName: invoice.billingSnapshot.customerName,
    billingSnapshot: invoice.billingSnapshot,
    billToSnapshot: invoice.billToSnapshot,
    invoiceDate: dateInput(invoice.issuedAtUtcMilli, timeZone),
    dueDate: invoice.dueAtUtcMilli ? dateInput(invoice.dueAtUtcMilli, timeZone) : "",
    lineItems: foldProvincialChamberFeeForDisplay({
      lineItems: invoice.lineItems,
      showSeparately: showProvincialChamberFeeSeparately,
    }).map((line) => ({
      id: line.id, code: line.itemCode, description: line.description,
      quantity: line.quantity, unitPriceInCents: line.unitPriceInCents,
      // Before tax, as on every invoice document: lines add up to Subtotal.
      totalInCents: line.subtotalAmountInCents,
    })),
    subtotalInCents: invoice.subtotalAmountInCents,
    taxInCents: invoice.taxAmountInCents,
    totalInCents: invoice.totalAmountInCents,
    taxLabel,
    taxRegistrationNumber,
    manualPaymentMethods: permittedPaymentMethods,
    publicMemo: invoice.publicMemo,
    referencePurchaseOrder: invoice.referencePurchaseOrder,
  } : null;
  return (
    <Dialog fullScreen open={open} onClose={busy ? undefined : onClose} PaperProps={{ sx: { background: "#EFF3F8" } }}>
      <InvoicePreviewPrintStyles />
      <div style={{ minHeight: "100vh", padding: "clamp(16px, 3vw, 40px)", fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ maxWidth: 1070, margin: "0 auto 12px", display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 12 }}>
          <InvoicePreviewZoomBar zoom={zoom} onZoomChange={setZoom} />
          <button type="button" aria-label="Close renewal invoice preview" disabled={busy} onClick={onClose} style={{ width: 28, height: 28, border: 0, borderRadius: "50%", background: "#F21B1B", color: "white" }}><svg width="13" height="13" viewBox="0 0 13 13" style={{ margin: "auto" }}><path d="M2 2l9 9M11 2l-9 9" stroke="currentColor" strokeWidth="3" /></svg></button>
        </div>
        <div style={{ maxWidth: 1070, margin: "0 auto 22px", display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 16 }}>
          <button type="button" style={{ ...actionStyle, background: "#0A0A0A", opacity: busy || loading || !preview || !canSaveDraft ? 0.5 : 1 }} disabled={busy || loading || !preview || !canSaveDraft} title={saveDraftReason || undefined} onClick={onSaveDraft}>Save Draft</button>
          <button type="button" style={{ ...actionStyle, opacity: busy || loading || !preview || !canApprove ? 0.5 : 1 }} disabled={busy || loading || !preview || !canApprove} title={approveReason || undefined} onClick={onApprove}>Approve/Create Invoice</button>
          <button type="button" style={{ ...actionStyle, opacity: busy || loading || !preview ? 0.5 : 1 }} disabled={busy || loading || !preview} onClick={printInvoicePreviewDocument}>Print PDF</button>
        </div>
        {error && <Alert severity="error" sx={{ maxWidth: 1070, margin: "0 auto 22px" }}>{error}</Alert>}
        {loading && <div role="status" style={{ display: "flex", justifyContent: "center", gap: 12 }}><CircularProgress size={20} />Loading invoice preview...</div>}
        {invoice && draftDocument && !loading && <div ref={viewportRef} style={{ maxWidth: 1070, margin: "0 auto", overflowX: "auto", overflowY: "hidden" }}>
          <div style={{ width: invoicePreviewPageWidth * scale, height: pageHeight ? pageHeight * scale : undefined, margin: "0 auto" }}>
            <div ref={pageRef} data-invoice-preview-page style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: invoicePreviewPageWidth }}>
              <InvoicePaper chamber={chamber} draftDocument={draftDocument} row={{
                id: invoice.id, customerName: draftDocument.customerName,
                invoiceKind: draftDocument.invoiceKind, invoiceNumber: invoice.invoiceNumber || "Draft",
                displayState: CmpInvoiceDisplayStateEnum.Draft, issuedAtUtcMilli: invoice.issuedAtUtcMilli,
                totalAmountInCents: draftDocument.totalInCents, balanceDueInCents: invoice.amountDueInCents,
                paymentRules: invoice.paymentRules || undefined, paymentPlan: invoice.paymentPlan,
              }} />
            </div>
          </div>
        </div>}
      </div>
    </Dialog>
  );
};

// "Show separate on invoice": the renewal preview shows the same lines as
// the invoice PDF. Read only with a chamber, the same as InvoicePaper.
const MemberRenewalInvoicePreviewWithPresentation = (
  props: Props & { chamber: NonNullable<Props["chamber"]> },
) => {
  const presentation = useGetCmpInvoicePresentationDefaultsQuery({
    variables: { input: { chamberId: props.chamber.id } },
    fetchPolicy: "cache-and-network",
  });
  const showProvincialChamberFeeSeparately =
    presentation.loading || presentation.error
      ? undefined
      : presentation.data?.getCmpInvoicePresentationDefaults
          .showProvincialChamberFeeSeparately;

  return (
    <MemberRenewalInvoicePreviewContent
      {...props}
      showProvincialChamberFeeSeparately={showProvincialChamberFeeSeparately}
    />
  );
};

const MemberRenewalInvoicePreview = (props: Props) =>
  props.chamber?.id ? (
    <MemberRenewalInvoicePreviewWithPresentation
      {...props}
      chamber={props.chamber}
    />
  ) : (
    <MemberRenewalInvoicePreviewContent {...props} />
  );

export default MemberRenewalInvoicePreview;
